import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Trash2 } from "lucide-react";
import { uploadImageUrl } from "@/lib/api";

const dialogCls = "bg-[#161617] border-white/10 text-white max-w-lg";

export const HelpDialog = ({ open, onOpenChange, limits }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={dialogCls} data-testid="help-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">How Image2STL works</DialogTitle>
        <DialogDescription className="text-zinc-400">From picture to printable file in four steps.</DialogDescription>
      </DialogHeader>
      <ol className="space-y-3 text-sm text-zinc-300 list-decimal pl-5">
        <li><b className="text-white">Upload</b> a PNG, JPG or WEBP (max {limits?.max_upload_mb} MB).</li>
        <li><b className="text-white">Choose a mode.</b> Relief (free, local): <i>Extrude</i> for logos/silhouettes, <i>Height map</i> for photos. AI Full 3D: a volumetric object via a cloud model.</li>
        <li><b className="text-white">Adjust</b> size, depth, base, resolution and click <i>Generate</i>.</li>
        <li><b className="text-white">Inspect & download.</b> Check printability warnings, then download STL/OBJ/GLB for free.</li>
      </ol>
      <div className="text-xs text-zinc-500 space-y-1 border-t border-white/5 pt-3">
        <p>Viewer: left-drag rotate · right-drag pan · scroll zoom.</p>
        <p>Limits: {limits?.relief_per_hour} relief conversions/hour, {limits?.ai_per_day} AI jobs/day. Files auto-delete after {limits?.retention_hours} h.</p>
      </div>
    </DialogContent>
  </Dialog>
);

export const PrivacyDialog = ({ open, onOpenChange, hours = 24 }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={dialogCls} data-testid="privacy-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">Privacy notice</DialogTitle>
        <DialogDescription className="text-zinc-400">Short version: your images stay yours.</DialogDescription>
      </DialogHeader>
      <ul className="space-y-2 text-sm text-zinc-300 list-disc pl-5">
        <li>No account is required. We do not collect names or emails.</li>
        <li>Uploaded images and generated models are stored under random IDs and automatically deleted after {hours} hours.</li>
        <li>Relief conversion runs entirely on our server; your image is not shared with third parties.</li>
        <li>AI Full 3D mode sends the image to the configured AI provider (fal.ai) solely to generate your model.</li>
        <li>Your images are never used to train models without explicit consent.</li>
        <li>"My projects" is stored only in your browser (localStorage).</li>
      </ul>
    </DialogContent>
  </Dialog>
);

export const ProjectsSheet = ({ open, onOpenChange, projects, onOpen, onRemove }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="left" className="bg-[#121213] border-white/10 text-white w-[340px]" data-testid="projects-sheet">
      <SheetHeader>
        <SheetTitle className="font-display text-white">My projects</SheetTitle>
        <SheetDescription className="text-zinc-500 text-xs">Saved in this browser. Server files expire after 24 h.</SheetDescription>
      </SheetHeader>
      <div className="mt-6 space-y-2 overflow-y-auto thin-scroll max-h-[80vh]">
        {projects.length === 0 && <p className="text-sm text-zinc-500" data-testid="projects-empty">No projects yet.</p>}
        {projects.map((p) => (
          <div key={p.model_id} className="flex items-center gap-3 p-2 rounded-xl border border-white/5 hover:border-white/20 transition-colors" data-testid={`project-item-${p.model_id}`}>
            <button type="button" className="flex items-center gap-3 flex-1 min-w-0 text-left" onClick={() => onOpen(p)} data-testid={`project-open-${p.model_id}`}>
              <img src={uploadImageUrl(p.upload_id)} alt="" className="h-12 w-12 rounded-lg object-cover bg-white/5" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
              <div className="min-w-0">
                <p className="text-sm truncate">{p.name}</p>
                <p className="text-[11px] text-zinc-500 font-mono">{p.kind.toUpperCase()} · {p.triangles?.toLocaleString()} tris</p>
                <p className="text-[11px] text-zinc-600">{new Date(p.created_at).toLocaleString()}</p>
              </div>
            </button>
            <button type="button" aria-label="Remove project" onClick={() => onRemove(p.model_id)} className="p-2 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`project-remove-${p.model_id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);
